import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Home, MapPin, Clock, XCircle, RefreshCw, MessageSquare, Ban, ListOrdered } from "lucide-react";
import api from "@/lib/api";
import { STEPS, currentStepIndex, statusBanner } from "@/lib/tracking";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PushToggle } from "@/components/PushToggle";
import { ChangeAddressDialog } from "@/components/ChangeAddressDialog";
import { toast } from "sonner";

const CANCEL_REASONS = ["Problème de paiement", "Attente trop longue", "Je ne veux plus la commande"];

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
    o.start(); o.stop(ctx.currentTime + 0.5);
  } catch (e) {}
}

function notify(title, body) {
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(title, { body });
    }
  } catch (e) {}
}

export default function Confirmation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const cart = useCart();
  const [order, setOrder] = useState(null);
  const [contactSent, setContactSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [addressOpen, setAddressOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const prevStep = useRef(-1);
  const toCatalog = () => navigate("/", { state: { browse: true } });

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        setOrder(data);
        const step = currentStepIndex(data);
        if (prevStep.current !== -1 && step > prevStep.current) {
          beep();
          if (step === 2) notify("Avenue Benda", data.exact_arrival_time ? `Votre livreur arrive à ${data.exact_arrival_time} — soyez dehors pour récupérer votre commande !` : `Votre livreur est en route${data.eta_minutes ? ` — ${data.eta_minutes} min` : ""} !`);
          if (step === 3) notify("Avenue Benda", "Votre livreur est arrivé !");
          if (step === 4) notify("Avenue Benda", "Votre commande a été livrée. Merci !");
        }
        prevStep.current = step;
        const { data: act } = await api.get("/orders/active");
        setActiveCount(act.length);
      } catch (e) {}
      timer = setTimeout(load, 5000);
    };
    load();
    return () => clearTimeout(timer);
  }, [id, reloadKey]);

  if (!order) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>;

  const cancelOrder = async (reason) => {
    setCancelling(true);
    try {
      const { data } = await api.post(`/orders/${id}/cancel`, { reason });
      setOrder(data);
      setCancelOpen(false);
      toast.success("Commande annulée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Annulation impossible");
    } finally { setCancelling(false); }
  };

  if (order.status === "cancelled") {
    const reorder = () => {
      cart.addMany(order.items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, image_url: i.image_url, quantity: i.quantity })));
      toast.success("Articles ajoutés au panier");
      toCatalog();
    };
    const requestContact = async () => {
      setSending(true);
      try { await api.post(`/orders/${id}/contact-request`); setContactSent(true); toast.success("Votre demande a été transmise au vendeur"); }
      catch (e) { toast.error("Impossible d'envoyer la demande, réessayez."); }
      finally { setSending(false); }
    };
    return (
      <div className="max-w-md mx-auto min-h-screen bg-[#08080C] border-x border-amber-500/10 px-6 py-8 flex flex-col">
        <div className="text-center fade-up">
          <div className="w-16 h-16 rounded-full bg-rose-500/15 border border-rose-500/40 flex items-center justify-center mx-auto mb-3">
            <XCircle className="w-8 h-8 text-rose-400" />
          </div>
          <h1 className="text-3xl font-bold text-slate-100">Commande annulée</h1>
          <p className="font-mono text-amber-400 text-lg mt-1" data-testid="order-number">{order.order_number}</p>
        </div>

        <div data-testid="cancelled-banner" className="mt-6 rounded-2xl border px-4 py-4 bg-rose-500/10 text-rose-200 border-rose-500/40 text-center">
          <p className="text-sm font-semibold">{order.cancelled_by === "client" ? "Vous avez annulé votre commande." : "Le vendeur a annulé votre commande."}</p>
          {order.cancel_reason ? (
            <p className="text-xs text-rose-100 mt-2" data-testid="cancel-reason">Motif : {order.cancel_reason}</p>
          ) : null}
          <p className="text-xs text-rose-300/80 mt-1">Vous pouvez repasser la même commande ou contacter le vendeur.</p>
        </div>

        <div className="glass rounded-2xl p-5 mt-4 space-y-2">
          {order.items.map((i, idx) => (
            <div key={idx} className="flex justify-between text-sm">
              <span className="text-slate-300">{i.quantity}× {i.name}</span>
              <span className="font-mono text-amber-300">{(i.price * i.quantity).toFixed(2)} €</span>
            </div>
          ))}
          <div className="flex justify-between pt-2 border-t border-amber-500/15">
            <span className="text-slate-200 font-semibold">Total</span>
            <span className="font-mono font-bold gold-text text-lg">{order.total.toFixed(2)} €</span>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <Button data-testid="reorder-button" onClick={reorder} className="w-full btn-gold rounded-xl py-6 text-base">
            <RefreshCw className="w-4 h-4 mr-2" /> Repasser la même commande
          </Button>
          {contactSent ? (
            <div data-testid="contact-sent" className="w-full text-center rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 py-4 text-sm font-semibold">
              Demande envoyée — le vendeur vous recontactera.
            </div>
          ) : (
            <Button data-testid="contact-vendor-button" onClick={requestContact} disabled={sending} variant="outline"
              className="w-full rounded-xl py-6 text-base border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <><MessageSquare className="w-4 h-4 mr-2" /> Contacter le vendeur</>}
            </Button>
          )}
          {activeCount > 0 && (
            <Button data-testid="my-orders-button" variant="outline" onClick={() => navigate("/orders")}
              className="w-full rounded-xl py-6 text-base border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
              <ListOrdered className="w-4 h-4 mr-2" /> Mes commandes en cours ({activeCount})
            </Button>
          )}
          <Button data-testid="back-home-button" variant="ghost" onClick={toCatalog} className="w-full text-slate-400 hover:text-amber-300">
            <Home className="w-4 h-4 mr-2" /> Retour au catalogue
          </Button>
        </div>
      </div>
    );
  }

  const payLabel = order.payment_method === "card" ? "Carte Bancaire (payée)" : "Espèces à la livraison";
  const step = currentStepIndex(order);

  const banner = statusBanner(order);

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#08080C] border-x border-amber-500/10 px-6 py-8 flex flex-col">
      <div className="text-center fade-up">
        <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="w-8 h-8 text-emerald-400" />
        </div>
        <h1 className="text-3xl font-bold text-slate-100">Commande validée</h1>
        <p className="font-mono text-amber-400 text-lg mt-1" data-testid="order-number">{order.order_number}</p>
        <p className="text-slate-400 text-sm mt-1">{payLabel}</p>
      </div>

      {/* Live status banner */}
      <div data-testid="live-status-banner" className={`mt-6 rounded-2xl border px-4 py-3 flex items-center gap-3 ${banner.cls}`}>
        <span className="relative flex h-3 w-3 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-60" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-current" />
        </span>
        <p className="text-sm font-semibold">{banner.text}</p>
      </div>
      <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Suivi en direct — gardez cette page ouverte. Mise à jour automatique.</p>

      <div className="mt-3"><PushToggle className="w-full justify-center py-3" /></div>
      <p className="text-[11px] text-slate-500 mt-1 text-center">Activez les notifications pour être prévenu « Votre livreur est en bas ! » même écran verrouillé.</p>

      {/* Timeline */}
      <div className="mt-6 glass rounded-2xl p-5">
        <div className="space-y-0">
          {STEPS.map((s, i) => {
            const done = i <= step;
            const active = i === step;
            const Icon = s.icon;
            return (
              <div key={s.key} data-testid={`track-step-${s.key}`} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all ${done ? "bg-amber-500/20 border-amber-500 text-amber-300" : "bg-slate-800/50 border-slate-700 text-slate-600"} ${active ? "ring-2 ring-amber-500/40" : ""}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  {i < STEPS.length - 1 && <div className={`w-0.5 h-8 ${i < step ? "bg-amber-500/60" : "bg-slate-700"}`} />}
                </div>
                <div className="pt-1.5">
                  <p className={`text-sm font-semibold ${done ? "text-slate-100" : "text-slate-500"}`}>{s.label}</p>
                  {s.key === "route" && active && (order.exact_arrival_time || order.eta_minutes) && (
                    <p className="text-xs text-amber-400 font-mono">{order.exact_arrival_time ? `Arrivée à ${order.exact_arrival_time} — soyez dehors` : `ETA ${order.eta_minutes >= 60 ? "1 h" : `${order.eta_minutes} min`}`}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Order recap */}
      <div className="glass rounded-2xl p-5 mt-4 space-y-2">
        {order.items.map((i, idx) => (
          <div key={idx} className="flex justify-between text-sm">
            <span className="text-slate-300">{i.quantity}× {i.name}</span>
            <span className="font-mono text-amber-300">{(i.price * i.quantity).toFixed(2)} €</span>
          </div>
        ))}
        <div className="flex justify-between pt-2 border-t border-amber-500/15">
          <span className="text-slate-400 text-sm">Sous-total</span>
          <span className="font-mono text-slate-300 text-sm">{(order.subtotal ?? order.total).toFixed(2)} €</span>
        </div>
        {order.delivery_fee > 0 && (
          <div className="flex justify-between">
            <span className="text-slate-400 text-xs">Frais de livraison</span>
            <span className="font-mono text-slate-300 text-sm">+{order.delivery_fee.toFixed(2)} €</span>
          </div>
        )}
        {order.card_fee > 0 && (
          <div className="flex justify-between">
            <span className="text-amber-300 text-xs">Frais de paiement par carte</span>
            <span className="font-mono text-amber-300 text-sm">+{order.card_fee.toFixed(2)} €</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-slate-200 font-semibold">Total</span>
          <span className="font-mono font-bold gold-text text-lg">{order.total.toFixed(2)} €</span>
        </div>
        <div className="text-xs text-slate-400 pt-1 flex items-start gap-1">
          <MapPin className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
          <span className="text-slate-300">{order.delivery_address}</span>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {["pending", "assigned", "in_transit"].includes(order.status) && !order.arrived && (
          <Button data-testid="change-address-button" variant="outline" onClick={() => setAddressOpen(true)}
            className="w-full rounded-xl py-6 text-base border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
            <MapPin className="w-4 h-4 mr-2" /> Changer d'adresse
          </Button>
        )}
        {order.status !== "delivered" && (
          <Button data-testid="client-cancel-button" variant="outline" onClick={() => setCancelOpen(true)}
            className="w-full rounded-xl py-6 text-base border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
            <Ban className="w-4 h-4 mr-2" /> Annuler la commande
          </Button>
        )}
        {activeCount > 1 && (
          <Button data-testid="my-orders-button" variant="outline" onClick={() => navigate("/orders")}
            className="w-full rounded-xl py-6 text-base border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
            <ListOrdered className="w-4 h-4 mr-2" /> Toutes mes commandes en cours ({activeCount})
          </Button>
        )}
        <Button data-testid="back-home-button" variant="ghost" onClick={toCatalog}
          className="w-full text-slate-400 hover:text-amber-300">
          <Home className="w-4 h-4 mr-2" /> Retour au catalogue
        </Button>
      </div>

      <ChangeAddressDialog order={order} open={addressOpen} onOpenChange={setAddressOpen} onChanged={() => setReloadKey((k) => k + 1)} />

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-dialog" className="bg-[#0F1320] border-amber-500/20 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Annuler la commande</DialogTitle>
            <DialogDescription className="text-slate-400">Choisissez un motif d'annulation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-1">
            {CANCEL_REASONS.map((r) => (
              <button key={r} data-testid={`cancel-reason-${r}`} disabled={cancelling} onClick={() => cancelOrder(r)}
                className="w-full text-left px-4 py-3 rounded-xl border border-amber-500/20 bg-[#161926] text-slate-200 hover:border-rose-500/40 hover:bg-rose-500/10 transition-all text-sm disabled:opacity-50">
                {r}
              </button>
            ))}
          </div>
          <Button data-testid="cancel-dialog-dismiss" variant="ghost" disabled={cancelling} onClick={() => setCancelOpen(false)}
            className="w-full text-slate-400 hover:text-amber-300 mt-1">
            {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Garder ma commande"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
